import { getErrorResponse } from "./errorUtils"
import { httpClient } from "./httpClient"
import type {
  CurrentUser,
  RawUserLastSeenAt,
  ReportReason,
  UpdateFromPrivyResponse,
  User,
  UserLastSeenAt,
} from "./types"
import {
  isSetNameError,
  isUserValidationError,
  normalizeSetNameError,
  normalizeUserValidationError,
} from "./userErrors"
import {
  formatAvatarUrl,
  formatCurrentUser,
  formatUser,
  formatUsers,
  toUnixMilliseconds,
} from "./utils"

async function getCurrentUser(): Promise<CurrentUser> {
  const response = await httpClient.get<CurrentUser>("/users/get-current-user")
  return formatCurrentUser(response.data)
}

async function saveUser(request: {
  name: string
  username: string
  bio: string
  language: string
  aiTranslatorEnabled: boolean
  callE2eeEnabled?: boolean
}): Promise<CurrentUser> {
  try {
    const response = await httpClient.post<CurrentUser>(
      "/users/save-user",
      request,
    )
    return formatCurrentUser(response.data)
  } catch (error) {
    throw normalizeUserValidationError(error)
  }
}

async function setName(request: { name: string }): Promise<CurrentUser> {
  try {
    const response = await httpClient.post<CurrentUser>(
      "/users/set-name",
      request,
    )
    return formatCurrentUser(response.data)
  } catch (error) {
    throw normalizeSetNameError(error)
  }
}

async function updateFromPrivy(): Promise<UpdateFromPrivyResponse> {
  const response = await httpClient.post<UpdateFromPrivyResponse>(
    "/users/update-from-privy",
  )

  return {
    changedUser: response.data.changedUser
      ? await formatCurrentUser(response.data.changedUser)
      : undefined,
  }
}

async function searchUsers(request: { query: string }): Promise<User[]> {
  const response = await httpClient.get<User[]>(`/users/search-users`, {
    params: request,
  })
  return formatUsers(response.data)
}

async function getUserById(userId: string): Promise<User | undefined> {
  const response = await httpClient.post<Record<User["id"], User>>(
    "/users/get-users-by-ids",
    [userId],
  )

  return formatUsers(Object.values(response.data))[0] || undefined
}

async function getUsersByIds(request: User["id"][]): Promise<User[]> {
  const response = await httpClient.post<Record<User["id"], User>>(
    "/users/get-users-by-ids",
    request,
  )
  return formatUsers(Object.values(response.data))
}

async function getUserByUsername(request: {
  username: string
}): Promise<User | null> {
  try {
    const response = await httpClient.get<User>("/users/get-by-username", {
      params: { q: request.username },
    })

    return formatUser(response.data)
  } catch (error) {
    if (getErrorResponse(error)?.status === 404) {
      return null
    }
    throw error
  }
}

async function uploadAvatar(image: File | Blob): Promise<{ file: string }> {
  const formData = new FormData()
  formData.append("image", image)
  const response = await httpClient.post<{ file: string }>(
    "/users/upload-avatar",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  )
  return { file: formatAvatarUrl(response.data.file) }
}

async function reportUser(request: {
  userId: string
  reason: ReportReason
}): Promise<void> {
  await httpClient.post("/users/send-report", request)
}

async function checkBlock(userId: string): Promise<{ blocked: boolean }> {
  const response = await httpClient.get<{ blocked: boolean }>(
    "/users/check-block",
    { params: { userId } },
  )
  return response.data
}

async function getUserNote(userId: User["id"]): Promise<{ note: string }> {
  const response = await httpClient.get<{ note: string }>("/users/note", {
    params: { userId },
  })
  return { note: response.data.note || "" }
}

async function saveUserNote(request: {
  userId: User["id"]
  note: string
}): Promise<void> {
  await httpClient.post("/users/note", request)
}

async function blockUser(request: { userId: User["id"] }): Promise<void> {
  await httpClient.post("/users/block-user", request)
}

async function unblockUser(request: { userId: User["id"] }): Promise<void> {
  await httpClient.post("/users/unblock-user", request)
}

async function deleteCurrentUser(): Promise<void> {
  await httpClient.delete("/user")
}

async function setActiveContext(
  context: "personal" | string,
): Promise<CurrentUser> {
  const response = await httpClient.post<CurrentUser>("/users/active-context", {
    context,
  })
  return formatCurrentUser(response.data)
}

async function getLastSeenAt(userIds: User["id"][]): Promise<UserLastSeenAt[]> {
  if (userIds.length === 0) return []

  const response = await httpClient.post<RawUserLastSeenAt[]>(
    "/users/get-last-seen-at",
    userIds,
  )

  return (response.data ?? [])
    .map((entry) => {
      const ms = toUnixMilliseconds(entry.lastSeenAt)
      if (ms == null) return null
      return {
        userId: entry.userID,
        lastSeenAt: ms,
      }
    })
    .filter((entry): entry is UserLastSeenAt => entry !== null)
}

export const userApi = {
  getCurrentUser,
  saveUser,
  setName,
  updateFromPrivy,
  searchUsers,
  getUserById,
  getUsersByIds,
  getUserByUsername,
  uploadAvatar,
  reportUser,
  checkBlock,
  getUserNote,
  saveUserNote,
  blockUser,
  unblockUser,
  deleteCurrentUser,
  getLastSeenAt,
  setActiveContext,
  isSetNameError,
  isUserValidationError,
}
